import React, { useEffect, useRef, useState, useCallback } from "react";
import "../App.css";
import {mobile} from "../services/mobileBridge";

const TILE = 20;
const COLS = 19;
const ROWS = 21;
const W = COLS * TILE;
const H = ROWS * TILE;
const SPEED = 1.5;

// 1 = wall, 0 = dot, 2 = empty, 3 = power pellet
const MAZE_TEMPLATE: number[][] = [
  [1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1],
  [1,0,0,0,0,0,0,0,0,1,0,0,0,0,0,0,0,0,1],
  [1,3,1,1,0,1,1,1,0,1,0,1,1,1,0,1,1,3,1],
  [1,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,1],
  [1,0,1,1,0,1,0,1,1,1,1,1,0,1,0,1,1,0,1],
  [1,0,0,0,0,1,0,0,0,1,0,0,0,1,0,0,0,0,1],
  [1,1,1,1,0,1,1,1,2,1,2,1,1,1,0,1,1,1,1],
  [2,2,2,1,0,1,2,2,2,2,2,2,2,1,0,1,2,2,2],
  [1,1,1,1,0,1,2,1,1,2,1,1,2,1,0,1,1,1,1],
  [2,2,2,2,0,2,2,1,2,2,2,1,2,2,0,2,2,2,2],
  [1,1,1,1,0,1,2,1,1,1,1,1,2,1,0,1,1,1,1],
  [2,2,2,1,0,1,2,2,2,2,2,2,2,1,0,1,2,2,2],
  [1,1,1,1,0,1,2,1,1,1,1,1,2,1,0,1,1,1,1],
  [1,0,0,0,0,0,0,0,0,1,0,0,0,0,0,0,0,0,1],
  [1,0,1,1,0,1,1,1,0,1,0,1,1,1,0,1,1,0,1],
  [1,3,0,1,0,0,0,0,0,0,0,0,0,0,0,1,0,3,1],
  [1,1,0,1,0,1,0,1,1,1,1,1,0,1,0,1,0,1,1],
  [1,0,0,0,0,1,0,0,0,1,0,0,0,1,0,0,0,0,1],
  [1,0,1,1,1,1,1,1,0,1,0,1,1,1,1,1,1,0,1],
  [1,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,1],
  [1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1],
];

type Dir = { x: number; y: number };
const DIRS: Record<string, Dir> = {
    up: { x: 0, y: -1 },
    down: { x: 0, y: 1 },
    left: { x: -1, y: 0 },
    right: { x: 1, y: 0 },
};

type Ghost = { x: number; y: number; dir: Dir; color: string; scared: number; eaten: number };

function cloneMaze(): number[][] {
    return MAZE_TEMPLATE.map(row => [...row]);
}

function canMove(maze: number[][], col: number, row: number): boolean {
    if (row < 0 || row >= ROWS) return false;
    if (col < 0) col = COLS - 1;
    if (col >= COLS) col = 0;
    return maze[row][col] !== 1;
}

function pickGhostDir(maze: number[][], ghost: Ghost, target: { x: number; y: number }): Dir {
    const options: Dir[] = [];
    const opposite = { x: -ghost.dir.x, y: -ghost.dir.y };
    for (const d of Object.values(DIRS)) {
        if (d.x === opposite.x && d.y === opposite.y) continue;
        const nc = Math.round(ghost.x) + d.x;
        const nr = Math.round(ghost.y) + d.y;
        if (canMove(maze, nc, nr)) options.push(d);
    }
    if (options.length === 0) return opposite;
    // Pick direction closest to target (or away if scared)
    const sign = ghost.scared > 0 ? -1 : 1;
    options.sort((a, b) => {
        const da = Math.hypot(ghost.x + a.x - target.x, ghost.y + a.y - target.y);
        const db = Math.hypot(ghost.x + b.x - target.x, ghost.y + b.y - target.y);
        return sign * (da - db);
    });
    return options[0];
}

function PacMan({ country }: { country: 'south-africa' | 'nigeria' }) {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [score, setScore] = useState(0);
    const [highScore, setHighScore] = useState(() => Number(localStorage.getItem("pacmanHi") || 0));
    const [started, setStarted] = useState(false);
    const [gameOver, setGameOver] = useState(false);
    const [won, setWon] = useState(false);
    const [dpadPos, setDpadPos] = useState<{ x: number; y: number }>(() => {
        try {
            const saved = JSON.parse(localStorage.getItem("dpadPos") || "null");
            if (saved && typeof saved.x === "number" && typeof saved.y === "number") return saved;
        } catch {}
        return { x: -1, y: 8 }; // -1 means "unset, use CSS default"
    });
    const dragRef = useRef({ active: false, startX: 0, startY: 0, startLeft: 0, startTop: 0 });
    const dpadRef = useRef<HTMLDivElement>(null);

    const stateRef = useRef({
        maze: cloneMaze(),
        pac: { x: 9, y: 15, dir: { x: 0, y: 0 } as Dir, nextDir: { x: 0, y: 0 } as Dir, mouth: 0 },
        ghosts: [
            { x: 9, y: 9, dir: DIRS.left, color: "#ff3b3b", scared: 0, eaten: 0 },
            { x: 8, y: 9, dir: DIRS.right, color: "#ffb8de", scared: 0, eaten: 0 },
            { x: 10, y: 9, dir: DIRS.up, color: "#00d4ff", scared: 0, eaten: 0 },
            { x: 9, y: 8, dir: DIRS.down, color: "#ffb347", scared: 0, eaten: 0 },
        ] as Ghost[],
        score: 0,
        dotsLeft: 0,
        dead: false,
        won: false,
        started: false,
        tick: 0,
    });

    const animRef = useRef<number>(0);
    const lastTimeRef = useRef<number>(0);
    const touchStart = useRef<{ x: number; y: number } | null>(null);

    const reset = useCallback(() => {
        const s = stateRef.current;
        s.maze = cloneMaze();
        s.pac = { x: 9, y: 15, dir: { x: 0, y: 0 }, nextDir: { x: 0, y: 0 }, mouth: 0 };
        s.ghosts = [
            { x: 9, y: 9, dir: DIRS.left, color: "#ff3b3b", scared: 0, eaten: 0 },
            { x: 8, y: 9, dir: DIRS.right, color: "#ffb8de", scared: 0, eaten: 0 },
            { x: 10, y: 9, dir: DIRS.up, color: "#00d4ff", scared: 0, eaten: 0 },
            { x: 9, y: 8, dir: DIRS.down, color: "#ffb347", scared: 0, eaten: 0 },
        ];
        s.score = 0;
        s.dotsLeft = s.maze.flat().filter(v => v === 0 || v === 3).length;
        s.dead = false;
        s.won = false;
        s.started = true;
        s.tick = 0;
        setScore(0);
        setGameOver(false);
        setWon(false);
        setStarted(true);
        if (animRef.current) cancelAnimationFrame(animRef.current);
        lastTimeRef.current = 0;
        animRef.current = requestAnimationFrame(loop);
    }, []);

    const loop = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d", { alpha: false });
        if (!ctx) return;
        const s = stateRef.current;
        const now = performance.now();
        const dt = lastTimeRef.current ? Math.min((now - lastTimeRef.current) / 16.67, 2) : 1;
        lastTimeRef.current = now;

        if (s.started && !s.dead && !s.won) {
            s.tick += dt;
            const step = SPEED * dt * 0.1;

            // Pac-Man movement
            const p = s.pac;
            const col = Math.round(p.x);
            const row = Math.round(p.y);

            // Try next direction — only snap when actually changing direction
            const wantToChange = p.nextDir.x !== p.dir.x || p.nextDir.y !== p.dir.y;
            if (wantToChange && (p.nextDir.x || p.nextDir.y) && canMove(s.maze, col + p.nextDir.x, row + p.nextDir.y)) {
                p.x = col;
                p.y = row;
                p.dir = { ...p.nextDir };
            }

            // Move in current direction
            const nc = p.x + p.dir.x * step;
            const nr = p.y + p.dir.y * step;
            if (canMove(s.maze, Math.round(nc), Math.round(nr)) || (p.dir.x === 0 && p.dir.y === 0)) {
                p.x = nc;
                p.y = nr;
            } else {
                p.x = Math.round(p.x);
                p.y = Math.round(p.y);
            }

            // Wrap tunnel
            if (p.x < -0.5) p.x = COLS - 0.5;
            if (p.x > COLS - 0.5) p.x = -0.5;

            // Eat dots
            const pc = Math.round(p.x);
            const pr = Math.round(p.y);
            if (pc >= 0 && pc < COLS && pr >= 0 && pr < ROWS) {
                const tile = s.maze[pr][pc];
                if (tile === 0) {
                    s.maze[pr][pc] = 2;
                    s.score += 10;
                    s.dotsLeft--;
                    setScore(s.score);
                } else if (tile === 3) {
                    s.maze[pr][pc] = 2;
                    s.score += 50;
                    s.dotsLeft--;
                    setScore(s.score);
                    for (const g of s.ghosts) g.scared = 200;
                }
            }

            if (s.dotsLeft <= 0) {
                s.won = true;
                setWon(true);
                if (s.score > highScore) {
                    setHighScore(s.score);
                    localStorage.setItem("pacmanHi", String(s.score));
                }
            }

            // Mouth animation
            p.mouth = (p.mouth + dt * 0.15) % (Math.PI * 2);

            // Ghost movement
            for (const g of s.ghosts) {
                if (g.scared > 0) g.scared -= dt;
                if (g.eaten > 0) {
                    g.eaten -= dt;
                    // Return to ghost house
                    const homeX = 9, homeY = 9;
                    if (Math.hypot(g.x - homeX, g.y - homeY) < 0.5) {
                        g.eaten = 0;
                        g.scared = 0;
                        g.x = homeX;
                        g.y = homeY;
                        g.dir = DIRS.up;
                    } else {
                        const gc = Math.round(g.x);
                        const gr = Math.round(g.y);
                        if (Math.abs(gc - g.x) < 0.05 && Math.abs(gr - g.y) < 0.05) {
                            g.x = gc; g.y = gr;
                            g.dir = pickGhostDir(s.maze, g, { x: homeX, y: homeY });
                        }
                        const enx = g.x + g.dir.x * dt * 0.2;
                        const eny = g.y + g.dir.y * dt * 0.2;
                        const enc = Math.round(enx);
                        const enr = Math.round(eny);
                        if ((enc !== gc || enr !== gr) && !canMove(s.maze, enc, enr)) {
                            g.x = gc; g.y = gr;
                            g.dir = pickGhostDir(s.maze, g, { x: homeX, y: homeY });
                        } else {
                            g.x = enx;
                            g.y = eny;
                        }
                        if (g.x < -0.5) g.x = COLS - 0.5;
                        if (g.x > COLS - 0.5) g.x = -0.5;
                    }
                    continue; // Skip normal AI and collision while eaten
                }

                const gc = Math.round(g.x);
                const gr = Math.round(g.y);

                // Change direction at intersections (ghost centered on a tile)
                if (Math.abs(gc - g.x) < 0.05 && Math.abs(gr - g.y) < 0.05) {
                    g.x = gc; g.y = gr; // snap to grid
                    const target = { x: p.x, y: p.y };
                    g.dir = pickGhostDir(s.maze, g, target);
                }

                const gs = (g.scared > 0 ? 0.4 : 0.55) * dt * 0.1;
                const nx = g.x + g.dir.x * gs;
                const ny = g.y + g.dir.y * gs;
                const nc = Math.round(nx);
                const nr = Math.round(ny);

                // Wall check: if moving into a new tile that's impassable, snap and repick
                if ((nc !== gc || nr !== gr) && !canMove(s.maze, nc, nr)) {
                    g.x = gc;
                    g.y = gr;
                    const target = { x: p.x, y: p.y };
                    g.dir = pickGhostDir(s.maze, g, target);
                } else {
                    g.x = nx;
                    g.y = ny;
                }

                // Wrap
                if (g.x < -0.5) g.x = COLS - 0.5;
                if (g.x > COLS - 0.5) g.x = -0.5;

                // Collision with Pac-Man
                const dist = Math.hypot(g.x - p.x, g.y - p.y);
                if (dist < 0.9) {
                    if (g.scared > 0) {
                        // Eat ghost — becomes eyes returning to ghost house
                        g.eaten = 180;
                        g.scared = 0;
                        s.score += 200;
                        setScore(s.score);
                        mobile.haptic("success");
                    } else {
                        s.dead = true;
                        mobile.haptic("gameover");
                        setGameOver(true);
                        if (s.score > highScore) {
                            setHighScore(s.score);
                            localStorage.setItem("pacmanHi", String(s.score));
                        }
                    }
                }
            }
        }

        // Draw
        draw(ctx, stateRef.current);
        animRef.current = requestAnimationFrame(loop);
    }, [highScore]);

    const draw = (ctx: CanvasRenderingContext2D, s: typeof stateRef.current) => {
        // Background
        ctx.fillStyle = "#0a0a14";
        ctx.fillRect(0, 0, W, H);

        // Maze
        for (let r = 0; r < ROWS; r++) {
            for (let c = 0; c < COLS; c++) {
                const tile = s.maze[r][c];
                const x = c * TILE;
                const y = r * TILE;
                if (tile === 1) {
                    ctx.fillStyle = "#1a1a4e";
                    ctx.fillRect(x, y, TILE, TILE);
                    ctx.fillStyle = "#3d3dcc";
                    ctx.fillRect(x, y, TILE, 2);
                    ctx.fillRect(x, y, 2, TILE);
                } else if (tile === 0) {
                    ctx.fillStyle = "#fde047";
                    ctx.beginPath();
                    ctx.arc(x + TILE / 2, y + TILE / 2, 2, 0, Math.PI * 2);
                    ctx.fill();
                } else if (tile === 3) {
                    ctx.fillStyle = "#fde047";
                    ctx.beginPath();
                    ctx.arc(x + TILE / 2, y + TILE / 2, 5, 0, Math.PI * 2);
                    ctx.fill();
                    ctx.shadowBlur = 8;
                    ctx.shadowColor = "#fde047";
                    ctx.fill();
                    ctx.shadowBlur = 0;
                }
            }
        }

        // Pac-Man
        const p = s.pac;
        const px = p.x * TILE + TILE / 2;
        const py = p.y * TILE + TILE / 2;
        const mouthOpen = (Math.sin(p.mouth) * 0.5 + 0.5) * 0.4 + 0.05;
        const angle = Math.atan2(p.dir.y, p.dir.x);
        ctx.save();
        ctx.translate(px, py);
        ctx.rotate(angle);
        ctx.fillStyle = "#fde047";
        ctx.beginPath();
        ctx.arc(0, 0, TILE / 2 - 1, mouthOpen, Math.PI * 2 - mouthOpen);
        ctx.lineTo(0, 0);
        ctx.fill();
        ctx.restore();

        // Ghosts
        for (const g of s.ghosts) {
            const gx = g.x * TILE + TILE / 2;
            const gy = g.y * TILE + TILE / 2;
            const r = TILE / 2 - 1;

            if (g.eaten > 0) {
                // Just eyes floating back to ghost house
                ctx.fillStyle = "#fff";
                ctx.beginPath();
                ctx.arc(gx - 4, gy - 2, 3, 0, Math.PI * 2);
                ctx.arc(gx + 4, gy - 2, 3, 0, Math.PI * 2);
                ctx.fill();
                ctx.fillStyle = "#0a0a14";
                const ex = g.dir.x * 1.5;
                const ey = g.dir.y * 1.5;
                ctx.beginPath();
                ctx.arc(gx - 4 + ex, gy - 2 + ey, 1.5, 0, Math.PI * 2);
                ctx.arc(gx + 4 + ex, gy - 2 + ey, 1.5, 0, Math.PI * 2);
                ctx.fill();
                continue;
            }

            ctx.fillStyle = g.scared > 0 ? (g.scared < 60 && Math.floor(s.tick / 10) % 2 ? "#fff" : "#3030ff") : g.color;
            // Body
            ctx.beginPath();
            ctx.arc(gx, gy - 1, r, Math.PI, 0);
            ctx.lineTo(gx + r, gy + r);
            // Wavy bottom
            for (let i = 0; i < 3; i++) {
                const wx = gx + r - (i * 2 + 1) * (r / 3);
                ctx.lineTo(wx, gy + r - 3);
                ctx.lineTo(wx - r / 3, gy + r);
            }
            ctx.lineTo(gx - r, gy + r);
            ctx.closePath();
            ctx.fill();
            // Eyes
            ctx.fillStyle = "#fff";
            ctx.beginPath();
            ctx.arc(gx - 4, gy - 2, 3, 0, Math.PI * 2);
            ctx.arc(gx + 4, gy - 2, 3, 0, Math.PI * 2);
            ctx.fill();
            // Pupils
            ctx.fillStyle = g.scared > 0 ? "#ff3b3b" : "#0a0a14";
            const ex = g.dir.x * 1.5;
            const ey = g.dir.y * 1.5;
            ctx.beginPath();
            ctx.arc(gx - 4 + ex, gy - 2 + ey, 1.5, 0, Math.PI * 2);
            ctx.arc(gx + 4 + ex, gy - 2 + ey, 1.5, 0, Math.PI * 2);
            ctx.fill();
        }
    };

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            const s = stateRef.current;
            switch (e.key) {
                case "ArrowUp": case "w": case "W": s.pac.nextDir = { ...DIRS.up }; e.preventDefault(); break;
                case "ArrowDown": case "s": case "S": s.pac.nextDir = { ...DIRS.down }; e.preventDefault(); break;
                case "ArrowLeft": case "a": case "A": s.pac.nextDir = { ...DIRS.left }; e.preventDefault(); break;
                case "ArrowRight": case "d": case "D": s.pac.nextDir = { ...DIRS.right }; e.preventDefault(); break;
                case " ":
                    e.preventDefault();
                    if (!s.started || s.dead || s.won) reset();
                    break;
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [reset]);

    useEffect(() => {
        animRef.current = requestAnimationFrame(loop);
        return () => { if (animRef.current) cancelAnimationFrame(animRef.current); };
    }, [loop]);

    const handleDpadDragStart = (e: React.PointerEvent) => {
        e.preventDefault();
        e.stopPropagation();
        const el = dpadRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        dragRef.current = {
            active: true,
            startX: e.clientX,
            startY: e.clientY,
            startLeft: rect.left,
            startTop: rect.top,
        };
        el.setPointerCapture(e.pointerId);
    };

    useEffect(() => {
        const onMove = (e: PointerEvent) => {
            if (!dragRef.current.active) return;
            const d = dragRef.current;
            const dx = e.clientX - d.startX;
            const dy = e.clientY - d.startY;
            const left = Math.max(0, Math.min(window.innerWidth - 180, d.startLeft + dx));
            const top = Math.max(0, d.startTop + dy);
            setDpadPos({ x: left, y: top });
        };
        const onUp = () => {
            if (!dragRef.current.active) return;
            dragRef.current.active = false;
            setDpadPos(prev => {
                localStorage.setItem("dpadPos", JSON.stringify(prev));
                return prev;
            });
        };
        window.addEventListener("pointermove", onMove);
        window.addEventListener("pointerup", onUp);
        return () => {
            window.removeEventListener("pointermove", onMove);
            window.removeEventListener("pointerup", onUp);
        };
    }, []);

    const handleTouchStart = (e: React.TouchEvent) => {
        const t = e.touches[0];
        touchStart.current = { x: t.clientX, y: t.clientY };
    };
    const handleTouchEnd = (e: React.TouchEvent) => {
        if (!touchStart.current) return;
        const t = e.changedTouches[0];
        const dx = t.clientX - touchStart.current.x;
        const dy = t.clientY - touchStart.current.y;
        const s = stateRef.current;
        if (Math.abs(dx) < 20 && Math.abs(dy) < 20) {
            if (!s.started || s.dead || s.won) reset();
            return;
        }
        if (Math.abs(dx) > Math.abs(dy)) {
            s.pac.nextDir = dx > 0 ? { ...DIRS.right } : { ...DIRS.left };
        } else {
            s.pac.nextDir = dy > 0 ? { ...DIRS.down } : { ...DIRS.up };
        }
        touchStart.current = null;
    };

    return (
        <div className="container page-dot-eater">
            <a href={`/game-studio/${country}`} className="game-back-link">&larr; Back to Games</a>
            <div className="score-display">Score: <span>{score}</span> &nbsp; Best: <span>{highScore}</span></div>
            <div className="dpad-hint" style={{ color: "var(--text-3)", fontSize: "0.65rem", margin: "0 0 2px" }}>
                Drag handle to move controls
            </div>
            <div className="game-canvas-wrapper">
            <div style={{ position: "relative", display: "inline-block", borderRadius: 16, overflow: "hidden" }}>
                <canvas
                    ref={canvasRef}
                    width={W}
                    height={H}
                    className="game"
                    style={{ touchAction: "none" }}
                    onTouchStart={handleTouchStart}
                    onTouchEnd={handleTouchEnd}
                    onClick={() => { const s = stateRef.current; if (!s.started || s.dead || s.won) reset(); }}
                />
                {!started && (
                    <div className="overlay">
                        <h2>🟡 Dot Eater</h2>
                        <p style={{ color: "var(--text-2)", fontSize: "0.85rem" }}>Use buttons, swipe, or arrow keys</p>
                        <button className="center-btn" onClick={reset}>▶ Start</button>
                    </div>
                )}
                {gameOver && (
                    <div className="overlay">
                        <h2>💀 Game Over</h2>
                        <p style={{ color: "var(--text-2)" }}>Score: {score}</p>
                        {score >= highScore && score > 0 && <p style={{ color: "var(--good)" }}>New Best!</p>}
                        <button className="center-btn" onClick={reset}>🔄 Play Again</button>
                    </div>
                )}
                {won && (
                    <div className="overlay">
                        <h2 style={{ color: "var(--good)" }}>🎉 You Win!</h2>
                        <p style={{ color: "var(--text-2)" }}>Score: {score}</p>
                        <button className="center-btn" onClick={reset}>🔄 Play Again</button>
                    </div>
                )}
            </div>
            </div>

            {/* D-Pad */}
            <div className="dpad"
                 ref={dpadRef}
                 style={dpadPos.x >= 0 ? { left: dpadPos.x, top: dpadPos.y, bottom: "auto", transform: "none", position: "fixed" } as React.CSSProperties : undefined}
                 onTouchStart={e => e.preventDefault()}
                 onTouchMove={e => e.preventDefault()}
            >
                <div className="dpad-handle"
                     onPointerDown={handleDpadDragStart}
                >⠿</div>
                <button className="dpad-btn dpad-btn-diamond"
                    onPointerDown={e => { e.preventDefault(); e.stopPropagation(); stateRef.current.pac.nextDir = { ...DIRS.up }; }}
                    aria-label="Up">▲</button>
                <div className="dpad-row">
                    <button className="dpad-btn dpad-btn-diamond"
                        onPointerDown={e => { e.preventDefault(); e.stopPropagation(); stateRef.current.pac.nextDir = { ...DIRS.left }; }}
                        aria-label="Left">◀</button>
                    <button className="dpad-btn dpad-btn-diamond"
                        onPointerDown={e => { e.preventDefault(); e.stopPropagation(); stateRef.current.pac.nextDir = { ...DIRS.down }; }}
                        aria-label="Down">▼</button>
                    <button className="dpad-btn dpad-btn-diamond"
                        onPointerDown={e => { e.preventDefault(); e.stopPropagation(); stateRef.current.pac.nextDir = { ...DIRS.right }; }}
                        aria-label="Right">▶</button>
                </div>
            </div>
        </div>
    );
}

export default PacMan;
